import React, { useRef, useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import axios from 'axios';
import Tesseract from 'tesseract.js';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { FaArrowLeft, FaArrowRight, FaSpinner, FaCheck } from "react-icons/fa";
import RegistrationSuccess from './RegistrationSuccess';
import StepProgressBar from './RegisterSteps/StepProgressBar';
import Step1_Organization from './RegisterSteps/Step1_Organization';
import Step2_Bank from './RegisterSteps/Step2_Bank';
import Step3_Tax from './RegisterSteps/Step3_Tax';
import Step4_Review from './RegisterSteps/Step4_Review';
import './Register.css';

GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Note: Console warnings are suppressed in production via utils/consoleSuppress.js
// PDF.js warnings (fake worker, indexing) will be automatically suppressed in production

const API_BASE_URL = 'https://django-backend-115705416374.asia-south1.run.app';
//const API_BASE_URL = 'http://localhost:8000';

export default function Register({ onBack }) {

    // -------------------------------------------------------------------------
    // REFS
    // -------------------------------------------------------------------------
    const panFileRef = useRef(null);
    const gstFileRef = useRef(null);
    const passbookRef = useRef(null);
    const chequeRef = useRef(null);
    const msmeRef = useRef(null);
    const ndaRef = useRef(null);
    const assessmentRef = useRef(null);
    const vendorCodeRef = useRef(null); // Validated vendor code storage

    // -------------------------------------------------------------------------
    // STATE
    // -------------------------------------------------------------------------
    const [currentStep, setCurrentStep] = useState(1);
    const [isSuccess, setIsSuccess] = useState(false); // Registration success state
    const [vendorCode, setVendorCode] = useState(null); // For success message
    const [isLoading, setIsLoading] = useState(false);
    const [isOcrLoading, setIsOcrLoading] = useState(false);
    const [isLinkBlocked, setIsLinkBlocked] = useState(false); // To block tampered URLs
    const [captcha, setCaptcha] = useState('');
    const [captchaInput, setCaptchaInput] = useState('');
    const [agreedTerms, setAgreedTerms] = useState(false);

    // Vendor Data State
    const [vendorData, setVendorData] = useState({
        firstName: '',
        lastName: '',
        gst_number: '',
        pan_tan: '',
        pan_tan_number_status: '',
        gst_number_status: '',
        msme_number_status: '',
        address: '',
        contactName: '',
        email: '',
        phone: '',
        account_number: '',
        ifsc_code: '',
        account_holder_name: '',
        msme_number: '',
        country: '',
        region: '',
        district: '',
        house_number: '',
        city: '',
        postal_code: '',
        bank_name: '',
        bank_country: '',
        bank_state: '',
        bank_address: '',
        bank_city: '',
        bank_branch: '',
        vat_number: '',
        iban_number: '',
        cin_number: '',

        registered_office_address_ext: '',
        bank_control_key: '', // SWIFT Code for STL-DIGITAL
        bank_account_name: '', // used for account type in stl digital
    });

    async function callGeminiRestAPI(file) {

        const API_KEY = "AIzaSyDmbfvn4173DRkjbuzQk6cmlkaizcGFnr8";
        // FIX: The model name must be preceded by 'models/' in the URL path
        const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${API_KEY}`;

        try {
            const base64Data = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result.split(',')[1]);
                reader.readAsDataURL(file);
            });

            const payload = {
                contents: [{
                    parts: [
                        {
                            text: `Extract bank details. 
                               Rules:
                               1. account_number: digits only.
                               2. ifsc_code: 11 characters, uppercase.
                               3. account_holder_name: Title Case.
                               Return ONLY JSON with keys: "account_number", "ifsc_code", "account_holder_name".`
                        },
                        { inline_data: { mime_type: file.type, data: base64Data } }
                    ]
                }],
                generationConfig: {
                    response_mime_type: "application/json",
                    temperature: 0.1
                }
            };

            const response = await fetch(API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (result.error) {
                throw new Error(result.error.message);
            }

            if (result.candidates && result.candidates[0].content.parts[0].text) {
                let jsonString = result.candidates[0].content.parts[0].text;
                const finalData = JSON.parse(jsonString.replace(/```json|```/g, "").trim());
                return finalData;
            }

            throw new Error("No candidates found in response");
        } catch (error) {
            throw error;
        }
    }
    const [vendorFiles, setVendorFiles] = useState({
        gst_file: null,
        pan_tan_file: null,
        passbook: null,
        canceled_cheque: null,
        msme_certificate: null,
        nda_document: null,
        assessment_report: null,
    });

    const [uploaded, setUploaded] = useState({
        pan: false,
        gst: false,
        passbook: false,
        cheque: false,
        msme: false,
        nda: false,
        assessment: false,
    });

    const [fileNames, setFileNames] = useState({
        pan: '',
        gst: '',
        passbook: '',
        cheque: '',
        msme: '',
        nda: '',
        assessment: '',
    });

    const [ocrFailed, setOcrFailed] = useState({
        pan: false,
        gst: false,
        passbook: false,
        cheque: false,
        msme: false
    });

    // Validated Info from Backend (Logo, Company Name, etc.)
    const [vendorOnboardInfo, setVendorOnboardInfo] = useState(null);

    // // -------------------------------------------------------------------------
    // // EFFECTS
    // // -------------------------------------------------------------------------
    // // Validate vendor_code from URL ONCE at page load - before allowing registration
    // useEffect(() => {
    //     const url = new URL(window.location.href);
    //     const code = url.searchParams.get('vendor_code');

    //     if (code) {
    //         // Validate vendor_code with backend before proceeding
    //         validateVendorCode(code);
    //     } else {
    //         // Check if we have a stored vendor code from a previous valid session
    //         const storedCode = localStorage.getItem('last_valid_vendor_code');

    //         if (storedCode) {
    //             console.log('🔄 Restoring vendor code from localStorage:', storedCode);
    //             // Restore the URL with the stored vendor code
    //             const newUrl = new URL(window.location.href);
    //             newUrl.searchParams.set('vendor_code', storedCode);
    //             window.history.replaceState(null, '', newUrl.toString());

    //             // Validate the restored code
    //             validateVendorCode(storedCode);
    //         } else {
    //             // No code in URL and no stored code - show error
    //             blockLinkAndResetState();
    //             Swal.fire({
    //                 icon: 'error',
    //                 title: 'Invalid Link',
    //                 text: 'Vendor code is missing. Please use the link provided in your email.',
    //                 allowOutsideClick: false,
    //                 allowEscapeKey: false,
    //                 confirmButtonColor: '#dc2626'
    //             });
    //         }
    //     }

    //     generateCaptcha();

    //     // Monitor URL changes in real-time to detect manual tampering
    //     let lastUrl = window.location.href;
    //     let lastSearch = window.location.search;
    //     const checkUrlChange = async () => {
    //         if (isLinkBlocked) {
    //             return;
    //         }
    //         const currentUrl = window.location.href;
    //         const currentSearch = window.location.search;

    //         // Check both href and search to catch all changes
    //         if (currentUrl !== lastUrl || currentSearch !== lastSearch) {
    //             lastUrl = currentUrl;
    //             lastSearch = currentSearch;

    //             // Extract vendor_code from multiple sources
    //             const currentCode = new URL(currentUrl).searchParams.get('vendor_code') ||
    //                 (currentSearch.includes('vendor_code=') ?
    //                     currentSearch.split('vendor_code=')[1]?.split('&')[0] : null);
    //             const validatedCode = vendorCodeRef.current;

    //             console.log('🔍 URL MONITORING: URL changed');
    //             console.log('Current vendor_code:', currentCode);
    //             console.log('Validated vendor_code:', validatedCode);

    //             // If URL changed and vendor_code doesn't match validated one
    //             if (validatedCode && currentCode !== validatedCode) {
    //                 // Restoring valid URL automatically
    //                 console.warn('⚠️ URL Tampering detected! Restoring valid URL...');

    //                 const newUrl = new URL(window.location.href);
    //                 newUrl.searchParams.set('vendor_code', validatedCode);
    //                 window.history.replaceState(null, '', newUrl.toString());

    //                 // Also ensure state is consistent
    //                 if (!vendorOnboardInfo) {
    //                     try {
    //                         const response = await axios.get(`${API_BASE_URL}/api/vendor/get-by-code/`, {
    //                             params: { vendor_code: validatedCode }
    //                         });
    //                         if (response.data && response.data.vendor_name) {
    //                             setVendorOnboardInfo(response.data);
    //                             setVendorCode(validatedCode);
    //                         }
    //                     } catch (err) {
    //                         console.error("Failed to restore vendor info", err);
    //                     }
    //                 }
    //                 return;
    //             }

    //             // If URL is correct but frozen values are missing, restore them
    //             if (validatedCode && currentCode === validatedCode && !vendorOnboardInfo) {
    //                 // If URL is changed back to correct code, restore frozen values
    //                 try {
    //                     const response = await axios.get(`${API_BASE_URL}/api/vendor/get-by-code/`, {
    //                         params: { vendor_code: validatedCode }
    //                     });
    //                     if (response.data && response.data.vendor_name) {
    //                         setVendorOnboardInfo(response.data);
    //                         setVendorCode(validatedCode);
    //                     }
    //                 } catch (err) {
    //                     // Error restoring frozen values - will be restored on reload
    //                 }
    //             }
    //         }
    //     };

    //     // Check URL changes periodically (every 100ms for very fast detection)
    //     const intervalId = setInterval(checkUrlChange, 100);

    //     // Also check on window focus (user might have changed URL in another tab/window)
    //     const handleFocus = () => {
    //         checkUrlChange();
    //     };
    //     window.addEventListener('focus', handleFocus);

    //     // Also check on popstate (browser back/forward)
    //     window.addEventListener('popstate', checkUrlChange);

    //     // Check when page becomes visible again
    //     const handleVisibilityChange = () => {
    //         if (document.visibilityState === 'visible') {
    //             checkUrlChange();
    //         }
    //     };
    //     document.addEventListener('visibilitychange', handleVisibilityChange);

    //     return () => {
    //         clearInterval(intervalId);
    //         window.removeEventListener('popstate', checkUrlChange);
    //         window.removeEventListener('focus', handleFocus);
    //         document.removeEventListener('visibilitychange', handleVisibilityChange);
    //     };
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []); // Run only once on mount

    useEffect(() => {

        if (process.env.NODE_ENV === "development") {
            console.log("Dev mode: skipping vendor_code validation");
            return;
        }

        const url = new URL(window.location.href);
        const code = url.searchParams.get('vendor_code');

        if (code) {
            validateVendorCode(code);
        } else {
            blockLinkAndResetState();
        }

    }, []);


    // -------------------------------------------------------------------------
    // TERMS & CONDITIONS LOGIC
    // -------------------------------------------------------------------------
    const [showTerms, setShowTerms] = useState(false);
    const [termsContent, setTermsContent] = useState('');
    const [loadingTerms, setLoadingTerms] = useState(false);

    const fetchTermsAndConditions = async () => {
        setLoadingTerms(true);
        setShowTerms(true);
        try {
            const companyName = vendorOnboardInfo?.company || 'Default';
            // Attempt to fetch company-specific terms from backend
            // Attempt to fetch company-specific terms from backend
            // Use correct endpoint that expects vendor_code
            const response = await axios.get(`${API_BASE_URL}/api/company/terms/`, {
                params: { vendor_code: vendorCodeRef.current }
            });

            if (response.data && response.data.terms) {
                setTermsContent(response.data.terms);
            } else {
                // FALLBACK if no specific content returned
                setTermsContent(`
                    <h2>Terms & Conditions</h2>
                    <p>Standard registration terms apply.</p>
                    <p>Please ensure all provided information is accurate and documents are valid.</p>
                `);
            }
        } catch (error) {
            console.warn("Backend T&C fetch failed, using fallback:", error);
            // Fallback content for and local/testing
            setTermsContent(`
                <h2>Vendor Registration Terms & Conditions</h2>
                <p>1. <strong>General:</strong> These terms govern the registration process for vendors.</p>
                <p>2. <strong>Data Privacy:</strong> We protect your business information as per policy.</p>
                <p>3. <strong>Compliance:</strong> Registration is subject to verification of all documents.</p>
                <p>4. <strong>Accuracy:</strong> You certify that all data provided is true and correct.</p>
                <p style="color: #666; font-style: italic;">(Terms fetched from system defaults)</p>
            `);
        } finally {
            setLoadingTerms(false);
        }
    };

    const handleCloseTerms = () => {
        setShowTerms(false);
    };


    useEffect(() => {
        // Auto-save on change
        if (vendorCodeRef.current) {
            const dataToSave = {
                vendorData,
                currentStep
            };
            localStorage.setItem(`vendor_regist_${vendorCodeRef.current}`, JSON.stringify(dataToSave));
        }
    }, [vendorData, currentStep]);

    // -------------------------------------------------------------------------
    // HELPERS
    // -------------------------------------------------------------------------
    const generateCaptcha = () => {
        const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        let result = '';
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setCaptcha(result);
    };

    const resetCaptchaField = () => {
        setCaptchaInput('');
        generateCaptcha();
    };

    const validateVendorCode = async (code) => {
        try {
            setIsLoading(true);
            // Updated endpoint to match backend 'get_vendor_by_code'
            const response = await axios.get(`${API_BASE_URL}/api/vendor/get-by-code/?vendor_code=${code}`);
            if (response.data) {
                // Backend returns: { vendor_code, vendor_name, company, registered_flag }
                // Map it to what the UI expects if necessary, or just set it directly
                // The UI seems to use: logo (derived), company (direct)

                const data = response.data;

                // Check if already registered
                if (data.registered_flag) {
                    setIsSuccess(true);
                    return;
                }

                // Set vendor code in state
                vendorCodeRef.current = data.vendor_code;
                setVendorCode(data.vendor_code);

                // Store vendor code in localStorage immediately when link is first opened
                // This ensures the correct code is stored for URL restoration
                localStorage.setItem('last_valid_vendor_code', data.vendor_code);
                console.log('✅ Stored vendor code in localStorage:', data.vendor_code);

                // Determine Logo based on Company
                let logoPath = '/Main.png'; // Default
                const companyName = data.company || '';

                if (companyName === 'VendorManagement_ONB' || companyName === 'ONB') {
                    logoPath = '/STL_Logo.png';
                } else if (companyName.toLowerCase().includes('invenia')) {
                    logoPath = '/Invenia.png';
                } else if (companyName.toLowerCase().includes('stl') && companyName.toLowerCase().includes('digital')) {
                    logoPath = '/Main.png';
                }

                setVendorOnboardInfo({
                    ...data,
                    logo: logoPath,
                    company: companyName
                });

                console.log("Vendor code validated successfully via get-by-code");
            }
        } catch (error) {
            console.error("Error validating vendor code:", error);

            // FAIL-SAFE: If validation failed (e.g. tampered URL), try to restore from localStorage
            const storedCode = localStorage.getItem('last_valid_vendor_code');
            if (storedCode && storedCode !== code) {
                console.log('🔄 Validation failed for current code. Restoring last valid code from localStorage:', storedCode);
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.set('vendor_code', storedCode);
                window.history.replaceState(null, '', newUrl.toString());
                // Retry validation with stored code
                validateVendorCode(storedCode);
                return;
            }

            blockLinkAndResetState();
            Swal.fire({
                icon: 'error',
                title: 'Invalid or Expired Link',
                text: 'This registration link is invalid or has expired. Please contact the administrator.',
                allowOutsideClick: false,
                allowEscapeKey: false
            });
        } finally {
            setIsLoading(false);
        }
    };

    const blockLinkAndResetState = () => {
        setIsLinkBlocked(true);
        setVendorOnboardInfo(null);
    };

    const validateName = (name, fieldName = 'Name') => {
        if (!name || !name.trim()) return { valid: false, error: 'Name is required' };
        // Allow letters, spaces, dots, and hyphens
        const nameRegex = /^[a-zA-Z\s.-]+$/;
        if (!nameRegex.test(name)) {
            return { valid: false, error: `${fieldName} should only contain letters, spaces, dots (.), and hyphens (-).` };
        }
        if (name.length < 2) return { valid: false, error: `${fieldName} must be at least 2 characters.` };
        return { valid: true };
    };

    const validateAddress = (address) => {
        if (!address || !address.trim()) return { valid: false, error: 'Address is required' };
        if (address.length < 5) return { valid: false, error: 'Address must be at least 5 characters.' };
        return { valid: true };
    };

    const validatePAN = (pan) => {
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (!panRegex.test(pan)) return { valid: false, error: 'Invalid PAN format. Example: ABCDE1234F' };
        return { valid: true };
    };

    const validateGST = (gst) => {
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!gstRegex.test(gst)) return { valid: false, error: 'Invalid GST format. Example: 22AAAAA0000A1Z5' };
        return { valid: true };
    };

    const handleVendorChange = (e) => {
        const { name, value } = e.target;
        const isStlDigital = vendorOnboardInfo?.company &&
            (vendorOnboardInfo.company.trim().replace('-', '').toLowerCase() === 'stldigital' ||
                vendorOnboardInfo.company.trim().toLowerCase() === 'stl-digital');

        // Basic validations or formatting if needed
        if (name === 'phone' && !/^\d*$/.test(value)) return; // Only numbers for phone

        // Postal Code: Max 10 digits
        if (name === 'postal_code') {
            if (!/^\d*$/.test(value)) return; // Only numbers
            if (value.length > 10) {
                Swal.fire({
                    toast: true,
                    position: 'top',
                    icon: 'warning',
                    title: 'Postal Code limit: 10 digits',
                    showConfirmButton: false,
                    timer: 3000
                });
                return;
            }
        }

        // Company Name (First Name for STL-DIGITAL): Max 40 characters
        if (name === 'firstName' && isStlDigital && value.length > 40) {
            Swal.fire({
                toast: true,
                position: 'top',
                icon: 'info',
                title: 'Limit: 40. Fill remaining in Ext field.',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }

        // Company Name Ext (Last Name for STL-DIGITAL): Max 40 characters
        if (name === 'lastName' && isStlDigital && value.length > 40) {
            Swal.fire({
                toast: true,
                position: 'top',
                icon: 'info',
                title: 'Limit: 40 chars for Company Name Ext.',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }

        // Address: Max 60 characters (for STL-DIGITAL)
        if (name === 'address' && isStlDigital && value.length > 60) {
            Swal.fire({
                toast: true,
                position: 'top',
                icon: 'info',
                title: 'Limit: 60. Fill remaining in Ext field.',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }

        // District: Max 40 characters
        if (name === 'district' && value.length > 40) {
            Swal.fire({
                toast: true,
                position: 'top',
                icon: 'warning',
                title: 'District limit: 40 chars',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }

        // House Number: Max 10 characters
        if (name === 'house_number' && value.length > 10) {
            Swal.fire({
                toast: true,
                position: 'top',
                icon: 'warning',
                title: 'House Number limit: 10 chars',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }

        setVendorData(prev => {
            const newData = { ...prev, [name]: value };

            // Reset region if country changes
            if (name === 'country') {
                newData.region = '';
            }

            // Reset bank_state (Bank Region) if bank_country changes
            if (name === 'bank_country') {
                newData.bank_state = '';
            }

            return newData;
        });
    };

    // -------------------------------------------------------------------------
    // FILE HANDLING & OCR
    // -------------------------------------------------------------------------
    const validateFileType = (file, allowXlsx = false) => {
        const allowedTypes = [
            'application/pdf',
            'image/jpeg',
            'image/jpg',
            'image/png'
        ];
        if (allowXlsx) {
            allowedTypes.push(
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
                'application/vnd.ms-excel', // xls
                'application/msword', // doc
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // docx
            );
        }

        if (!allowedTypes.includes(file.type)) {
            // Fallback helper for file extension check if mimetype fails (sometimes happens on windows)
            const ext = file.name.split('.').pop().toLowerCase();
            const allowedExts = ['pdf', 'jpg', 'jpeg', 'png'];
            if (allowXlsx) allowedExts.push('xlsx', 'xls', 'doc', 'docx');

            if (!allowedExts.includes(ext)) {
                return { valid: false, error: 'Invalid file type.' };
            }
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            return { valid: false, error: 'File size must be less than 5MB.' };
        }
        return { valid: true };
    };

    // ... (Keep existing simple OCR helper functions intact) ...
    const isMostlyEmptyText = (text) => !text || text.trim().length < 20;

    const extractPanFromText = (text) => {
        const cleaned = text.toUpperCase();
        const panRegex = /\b([A-Z]{5}[0-9]{4}[A-Z])\b/;
        const match = cleaned.match(panRegex);
        return match ? match[0] : null;
    };

    const extractPassbookInfo = (text) => {
        const rawText = String(text || "")
            .toUpperCase()
            .replace(/[\n\r\t]+/g, " ")
            .replace(/\s{2,}/g, " ")
            .replace(/[^\x20-\x7E]/g, "")
            .trim();

        // eslint-disable-next-line no-useless-escape
        const ifscRegex = /IFSC(?:\s*CODE)?\s*[:\-]?\s*([A-Z0-9]{4}[0O][A-Z0-9]{6})/i;
        let ifscMatch = rawText.match(ifscRegex);
        let ifsc = ifscMatch ? ifscMatch[1].replace(/O/g, "0") : null;

        // eslint-disable-next-line no-useless-escape
        const accRegex = /(?:A(?:C{1,2}|\/C|C\/|C\.)\s*(?:NO|N0|NUMBER)?|ACC(?:OUNT)?\s*(?:NO|N0|NUMBER)?|BANK\s*ACCOUNT\s*(?:NO|N0|NUMBER)?)\s*[:\-]?\s*([0-9]{6,20})/i;

        let accMatch = rawText.match(accRegex);
        let accountNumber = accMatch ? accMatch[1] : null;

        if (!accountNumber) {
            const fallback = rawText.match(/\b\d{9,18}\b/);
            if (fallback) accountNumber = fallback[0];
        }

        let accountHolderName = null;
        // eslint-disable-next-line no-useless-escape
        const namePatterns = [
            /(?:CUSTOMER\s*NAME|ACCOUNT\s*HOLDER\s*NAME|NAME)\s*[:\-]?\s*([A-Z][A-Z\s]{2,50}?)(?:\s*(?:A\/C|ACCOUNT|IFSC|BRANCH|ADDRESS|\d))/i,
            /(?:NAME\s*OF\s*(?:ACCOUNT\s*)?HOLDER)\s*[:\-]?\s*([A-Z][A-Z\s]{2,50}?)(?:\s*(?:A\/C|ACCOUNT|IFSC|BRANCH|ADDRESS|\d))/i,
            /(?:HOLDER\s*NAME)\s*[:\-]?\s*([A-Z][A-Z\s]{2,50}?)(?:\s*(?:A\/C|ACCOUNT|IFSC|BRANCH|ADDRESS|\d))/i,
            /(?:NAME)\s*[:\-]\s*([A-Z][A-Z\s]{2,50}?)(?:\s*(?:A\/C|ACCOUNT|IFSC|BRANCH|ADDRESS|\d))/i
        ];

        for (const pattern of namePatterns) {
            const match = rawText.match(pattern);
            if (match && match[1]) {
                accountHolderName = match[1].trim();
                accountHolderName = accountHolderName
                    .split(/\s+/)
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ');
                break;
            }
        }

        const isValid = !!ifsc && !!accountNumber;
        return { ifsc, accountNumber, accountHolderName, isValid };
    };

    const extractMsmeNumber = (text) => {
        const cleaned = text.toUpperCase().replace(/\s+/g, " ");
        const regex = /UDYAM[\s:-]*[A-Z]{2}[-]?\d{2}[-]?\d{7}/i;
        const match = cleaned.match(regex);
        return match ? match[0].replace(/\s/g, "") : null;
    };

    const preprocessImage = async (file) => {
        return new Promise((resolve) => {
            const img = new Image();
            const reader = new FileReader();

            reader.onload = (e) => {
                img.src = e.target.result;
            };

            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                const scale = 2.0;
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;

                ctx.filter = "grayscale(100%) contrast(120%) brightness(105%)";
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                resolve(canvas.toDataURL("image/png"));
            };
            reader.readAsDataURL(file);
        });
    };

    const extractTextFromPdf = async (file) => {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await getDocument({ data: arrayBuffer }).promise;
            let fullText = "";

            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                fullText += textContent.items.map(item => item.str).join(" ") + "\n";
            }

            if (isMostlyEmptyText(fullText)) {
                // OCR fallback for scanned PDF
                const canvas = document.createElement("canvas");
                const page = await pdf.getPage(1);
                const viewport = page.getViewport({ scale: 2.0 });
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const ctx = canvas.getContext("2d");
                await page.render({ canvasContext: ctx, viewport }).promise;
                const { data: { text: ocrText } } = await Tesseract.recognize(canvas.toDataURL(), 'eng');
                fullText += ocrText;
            }

            return fullText;
        } catch (err) {
            // PDF OCR fallback error, using image preprocessing
            const preprocessed = await preprocessImage(file);
            const { data: { text } } = await Tesseract.recognize(preprocessed, 'eng');
            return text;
        }
    };

    const handleFileChange = async (e, sourceField) => {
        const file = e.target.files[0];
        if (!file) return;

        if (sourceField === 'passbook' || sourceField === 'canceled_cheque') {

            const uploadKey = sourceField === 'passbook' ? 'passbook' : 'cheque';
            const fileKey = sourceField === 'passbook' ? 'passbook' : 'canceled_cheque';

            // Update UI immediately
            setVendorFiles(prev => ({ ...prev, [fileKey]: file }));
            setFileNames(prev => ({ ...prev, [uploadKey]: file.name }));

            try {
                const data = await callGeminiRestAPI(file);
                if (data && (data.account_number || data.ifsc_code)) {
                    setVendorData(prev => ({
                        ...prev,
                        account_number: data.account_number || prev.account_number || '',
                        ifsc_code: data.ifsc_code || prev.ifsc_code || '',
                        account_holder_name: data.account_holder_name || prev.account_holder_name || ''
                    }));
                    setUploaded(prev => ({ ...prev, [uploadKey]: true }));
                    setOcrFailed(prev => ({ ...prev, [uploadKey]: false }));
                }
            } catch (error) {
                setOcrFailed(prev => ({ ...prev, [uploadKey]: true }));
            }
        }
        const validation = validateFileType(file, false);
        if (!validation.valid) {
            Swal.fire({ icon: 'error', title: 'Invalid File', text: validation.error });
            e.target.value = "";
            return;
        }

        setIsOcrLoading(true);
        let postProcessAlert = null;
        let ocrTimeout = null;

        try {
            let extractedText = "";

            // Create OCR processing promise
            const ocrPromise = (async () => {
                if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
                    return await extractTextFromPdf(file);
                } else {
                    const preprocessed = await preprocessImage(file);
                    const { data: { text } } = await Tesseract.recognize(preprocessed, 'eng');
                    return text;
                }
            })();

            // Create a timeout promise that rejects after 5 seconds
            const timeoutPromise = new Promise((_, reject) => {
                ocrTimeout = setTimeout(() => {
                    reject(new Error('OCR_TIMEOUT'));
                }, 5000);
            });

            // Race between OCR and timeout
            try {
                extractedText = await Promise.race([ocrPromise, timeoutPromise]);
                if (ocrTimeout) clearTimeout(ocrTimeout);
            } catch (timeoutError) {
                if (ocrTimeout) clearTimeout(ocrTimeout);
                if (timeoutError.message === 'OCR_TIMEOUT') {
                    throw new Error('OCR_TIMEOUT');
                }
                throw timeoutError;
            }

            // Logic to handle extracted text
            if (sourceField === 'pan_tan_file') {
                const pan = extractPanFromText(extractedText);
                setVendorFiles(prev => ({ ...prev, pan_tan_file: file }));
                setUploaded(prev => ({ ...prev, pan: true }));
                setFileNames(prev => ({ ...prev, pan: file.name }));
                if (pan) {
                    setVendorData(prev => ({ ...prev, pan_tan: pan }));
                    setOcrFailed(prev => ({ ...prev, pan: false }));
                } else {
                    setOcrFailed(prev => ({ ...prev, pan: true }));
                    postProcessAlert = {
                        icon: 'warning',
                        title: 'PAN/TAN Not Found',
                        text: 'File saved, but could not extract PAN/TAN. Please enter it manually.',
                        confirmButtonText: 'OK'
                    };
                }
            } else if (sourceField === 'gst_file') {
                const gstRegex = /[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]/;
                const match = extractedText.replace(/\s/g, '').toUpperCase().match(gstRegex);
                setVendorFiles(prev => ({ ...prev, gst_file: file }));
                setUploaded(prev => ({ ...prev, gst: true }));
                setFileNames(prev => ({ ...prev, gst: file.name }));
                if (match) {
                    setVendorData(prev => ({ ...prev, gst_number: match[0] }));
                    setOcrFailed(prev => ({ ...prev, gst: false }));
                } else {
                    setOcrFailed(prev => ({ ...prev, gst: true }));
                    postProcessAlert = {
                        icon: 'warning',
                        title: 'GST Not Found',
                        text: 'File saved, but could not extract GST. Please enter it manually.',
                        confirmButtonText: 'OK'
                    };
                }
            }
            else if (sourceField === 'passbook' || sourceField === 'canceled_cheque') {
                const uploadKey = sourceField === 'passbook' ? 'passbook' : 'cheque';
                const fileKey = sourceField === 'passbook' ? 'passbook' : 'canceled_cheque';

                setVendorFiles(prev => ({ ...prev, [fileKey]: file }));
                setFileNames(prev => ({ ...prev, [uploadKey]: file.name }));

                try {

                    const data = await callGeminiRestAPI(file);

                    if (data && (data.account_number || data.ifsc_code)) {

                        setVendorData(prev => {
                            const newState = {
                                ...prev,
                                account_number: data.account_number || prev.account_number || '',
                                ifsc_code: data.ifsc_code || prev.ifsc_code || '',
                                account_holder_name: data.account_holder_name || prev.account_holder_name || ''
                            };

                            return newState;
                        });

                        setUploaded(prev => ({ ...prev, [uploadKey]: true }));
                        setOcrFailed(prev => ({ ...prev, [uploadKey]: false }));
                        console.log("Step 2: UI flags updated (OCR Success)");

                        postProcessAlert = null;
                    } else {
                        throw new Error("Missing fields");
                    }

                } catch (error) {
                
                    setUploaded(prev => ({ ...prev, [uploadKey]: true }));
                    setOcrFailed(prev => ({ ...prev, [uploadKey]: true }));

                    postProcessAlert = {
                        icon: 'warning',
                        title: 'Manual Entry Required',
                        text: `We saved the file, but couldn't read the bank details. Please fill them manually.`,
                        confirmButtonText: 'OK'
                    };
                }
            } else if (sourceField === 'msme_certificate') {
                const msme = extractMsmeNumber(extractedText);
                setVendorFiles(prev => ({ ...prev, msme_certificate: file }));
                setUploaded(prev => ({ ...prev, msme: true }));
                setFileNames(prev => ({ ...prev, msme: file.name }));
                if (msme) {
                    setVendorData(prev => ({ ...prev, msme_number: msme }));
                    setOcrFailed(prev => ({ ...prev, msme: false }));
                } else {
                    setOcrFailed(prev => ({ ...prev, msme: true }));
                    postProcessAlert = {
                        icon: 'warning',
                        text: 'File saved, but could not extract MSME Number. Please enter it manually.',
                        confirmButtonText: 'OK'
                    };
                }
            }

        } catch (error) {
            // Error Handling (Timestamp or Logic error)
            // Just mark as uploaded but OCR failed
            if (sourceField === 'pan_tan_file') {
                setVendorFiles(prev => ({ ...prev, pan_tan_file: file }));
                setUploaded(prev => ({ ...prev, pan: true }));
                setFileNames(prev => ({ ...prev, pan: file.name }));
                setOcrFailed(prev => ({ ...prev, pan: true }));
            } else if (sourceField === 'gst_file') {
                setVendorFiles(prev => ({ ...prev, gst_file: file }));
                setUploaded(prev => ({ ...prev, gst: true }));
                setFileNames(prev => ({ ...prev, gst: file.name }));
                setOcrFailed(prev => ({ ...prev, gst: true }));
            } else if (sourceField === 'passbook') {
                setVendorFiles(prev => ({ ...prev, passbook: file }));
                setUploaded(prev => ({ ...prev, passbook: true }));
                setFileNames(prev => ({ ...prev, passbook: file.name }));
                setOcrFailed(prev => ({ ...prev, passbook: true }));
            } else if (sourceField === 'canceled_cheque') {
                setVendorFiles(prev => ({ ...prev, canceled_cheque: file }));
                setUploaded(prev => ({ ...prev, cheque: true }));
                setFileNames(prev => ({ ...prev, cheque: file.name }));
                setOcrFailed(prev => ({ ...prev, cheque: true }));
            } else if (sourceField === 'msme_certificate') {
                setVendorFiles(prev => ({ ...prev, msme_certificate: file }));
                setUploaded(prev => ({ ...prev, msme: true }));
                setFileNames(prev => ({ ...prev, msme: file.name }));
                setOcrFailed(prev => ({ ...prev, msme: true }));
            }

            let msg = 'File saved, but could not extract details. Please fill manually.';
            if (sourceField === 'pan_tan_file') msg = 'PAN/TAN File Saved , But Could Not Fetch PAN Number , So , Please Fill Manually .';
            else if (sourceField === 'gst_file') msg = 'GST File Saved , But Could Not Fetch GST Number , So , Please Fill Manually .';
            else if (sourceField === 'passbook') msg = 'Passbook Saved , But Could Not Fetch Account Details , So , Please Fill Manually .';
            else if (sourceField === 'canceled_cheque') msg = 'Cheque Saved , But Could Not Fetch Account Details , So , Please Fill Manually .';
            else if (sourceField === 'msme_certificate') msg = 'MSME Certificate Saved , But Could Not Fetch MSME Number , So , Please Fill Manually .';

            postProcessAlert = {
                icon: 'warning',
                text: msg,
                confirmButtonText: 'OK'
            };
        } finally {
            if (ocrTimeout) clearTimeout(ocrTimeout);
            setIsOcrLoading(false);
            if (postProcessAlert) {
                Swal.fire(postProcessAlert);
            }
        }
    };

    const handleStandardFileChange = (e, type, shortType, allowXlsx = false) => {
        const file = e.target.files[0];
        if (!file) return;

        const validation = validateFileType(file, allowXlsx);
        if (!validation.valid) {
            Swal.fire({ icon: 'error', title: 'Invalid File', text: validation.error });
            e.target.value = "";
            return;
        }

        setVendorFiles(prev => ({ ...prev, [type]: file }));
        setUploaded(prev => ({ ...prev, [shortType]: true }));
        setFileNames(prev => ({ ...prev, [shortType]: file.name }));
    };

    const handleRemoveFile = (type) => {
        const map = {
            pan: 'pan_tan_file', gst: 'gst_file', passbook: 'passbook',
            cheque: 'canceled_cheque', msme: 'msme_certificate', nda: 'nda_document',
            assessment: 'assessment_report',
        };
        const refMap = {
            pan: panFileRef, gst: gstFileRef, passbook: passbookRef,
            cheque: chequeRef, msme: msmeRef, nda: ndaRef, assessment: assessmentRef,
        };

        setVendorFiles(prev => ({ ...prev, [map[type]]: null }));
        setUploaded(prev => ({ ...prev, [type]: false }));
        setFileNames(prev => ({ ...prev, [type]: '' }));
        setOcrFailed(prev => ({ ...prev, [type]: false }));

        if (refMap[type]?.current) refMap[type].current.value = "";

        // Reset data fields if file is removed? (Maybe not, user might have edited them)
        // Original code reset them, so we should too to avoid mismatch.
        if (type === 'pan') setVendorData(prev => ({ ...prev, pan_tan: '' }));
        if (type === 'gst') setVendorData(prev => ({ ...prev, gst_number: '' }));
        if (type === 'msme') setVendorData(prev => ({ ...prev, msme_number: '' }));
        if (type === 'passbook' || type === 'cheque') {
            // Only reset if NO bank doc is present? No, specific file reset.
            // But data is shared. If I remove passbook, do I clear bank info?
            // Original: Yes.
            setVendorData(prev => ({
                ...prev,
                account_number: '',
                ifsc_code: '',
                account_holder_name: '',
                bank_name: '',
                bank_country: '', // Should probably keep these if manually selected? Original cleared.
                bank_state: '',
                bank_address: '',
                bank_city: '',
                bank_branch: ''
            }));
        }
    };

    // -------------------------------------------------------------------------
    // STEP VALIDATION
    // -------------------------------------------------------------------------

    const validateStep1 = () => {
        const isStlDigitalForMandatory = vendorOnboardInfo?.company && (vendorOnboardInfo.company.trim().replace('-', '').toLowerCase() === 'stldigital' || vendorOnboardInfo.company.trim().toLowerCase() === 'stl-digital');

        let mandatoryFields = [
            { key: 'firstName', label: isStlDigitalForMandatory ? 'Company Name' : 'First Name' },
            ...(isStlDigitalForMandatory ? [] : [{ key: 'lastName', label: 'Last Name' }]),
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone Number' },
            { key: 'address', label: 'Registered Office Address' },
            { key: 'country', label: 'Country' },
            { key: 'city', label: 'City' },
            { key: 'postal_code', label: 'Postal Code' },
            { key: 'region', label: 'Region' }
        ];

        // STL-DIGITAL Specific
        if (isStlDigitalForMandatory && (vendorData.address?.length >= 60 || vendorData.registered_office_address_ext) && (!vendorData.registered_office_address_ext || !vendorData.registered_office_address_ext.trim())) {
            mandatoryFields.push({ key: 'registered_office_address_ext', label: 'Extended Address' });
        }

        const missing = mandatoryFields.filter(field => !(vendorData[field.key] || '').trim());
        if (missing.length > 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Fields',
                text: `Please fill: ${missing.map(f => f.label).join(', ')}`
            });
            return false;
        }

        // Specific Validations
        const firstNameVal = validateName(vendorData.firstName, 'First Name');
        if (!firstNameVal.valid) { Swal.fire({ icon: 'error', text: firstNameVal.error }); return false; }

        if (!isStlDigitalForMandatory) {
            const lastNameVal = validateName(vendorData.lastName, 'Last Name');
            if (!lastNameVal.valid) { Swal.fire({ icon: 'error', text: lastNameVal.error }); return false; }
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(vendorData.email.trim())) {
            Swal.fire({ icon: 'error', text: 'Invalid Email Address' }); return false;
        }

        if (!/^\d{10}$/.test(vendorData.phone.trim())) {
            Swal.fire({ icon: 'error', text: 'Phone number must be exactly 10 digits.' }); return false;
        }

        const addrVal = validateAddress(vendorData.address);
        if (!addrVal.valid) { Swal.fire({ icon: 'error', text: addrVal.error }); return false; }

        return true;
    };

    const validateStep2 = () => {
        // Bank Details
        const isIndia = vendorData.country === 'IN' || !vendorData.country;

        // At least one doc check
        if (!uploaded.passbook && !uploaded.cheque) {
            Swal.fire({ icon: 'warning', text: 'Please upload either Passbook or Cancelled Cheque.' });
            return false;
        }

        // Fields check
        const fields = [
            { key: 'account_number', label: 'Account Number' },
            { key: 'ifsc_code', label: 'IFSC Code' },
            { key: 'account_holder_name', label: 'Account Holder Name' }
        ];

        // Conditional Mandatory for Bank Details if India
        if (isIndia) {
            fields.push(
                { key: 'bank_name', label: 'Bank Name' },
                { key: 'bank_address', label: 'Bank Address' },
                { key: 'bank_city', label: 'Bank City' },
                { key: 'bank_country', label: 'Bank Country' },
                { key: 'bank_state', label: 'Bank Region' }
            );
            // STL non-digital might need branch
            const isStlDigital = vendorOnboardInfo?.company && (vendorOnboardInfo.company.trim().replace('-', '').toLowerCase() === 'stldigital' || vendorOnboardInfo.company.trim().toLowerCase() === 'stl-digital');
            if (!isStlDigital) {
                fields.push({ key: 'bank_branch', label: 'Bank Branch' });
            } else {
                // STL Digital specific
                // fields.push({ key: 'bank_key', label: 'Bank Key' }); // Made Optional
            }
        } else {
            // Foreign Vendor Logic
            const isStlDigital = vendorOnboardInfo?.company && (vendorOnboardInfo.company.trim().replace('-', '').toLowerCase() === 'stldigital' || vendorOnboardInfo.company.trim().toLowerCase() === 'stl-digital');

            if (isStlDigital) {
                // STL-DIGITAL Foreign Vendors: Strict Validation
                fields.push(
                    { key: 'bank_name', label: 'Bank Name' },
                    { key: 'bank_address', label: 'Bank Address' },
                    { key: 'bank_city', label: 'Bank City' },
                    { key: 'bank_country', label: 'Bank Country' }
                );
            }
        }

        const missing = fields.filter(field => !(vendorData[field.key] || '').trim());
        if (missing.length > 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Bank Details',
                text: `Please fill: ${missing.map(f => f.label).join(', ')}`
            });
            return false;
        }

        return true;
    };

    const validateStep3 = () => {
        const isIndia = vendorData.country === 'IN' || !vendorData.country;
        const isStlDigital = vendorOnboardInfo?.company && (vendorOnboardInfo.company.trim().replace('-', '').toLowerCase() === 'stldigital' || vendorOnboardInfo.company.trim().toLowerCase() === 'stl-digital');

        let missingDocs = [];

        if (isIndia) {
            if (!uploaded.pan) missingDocs.push('PAN/TAN Document');
            if (!isStlDigital && !uploaded.gst) missingDocs.push('GST Document');
            if (!isStlDigital && !uploaded.msme) missingDocs.push('MSME Certificate');
        }

        if (!isStlDigital && !uploaded.assessment) missingDocs.push('Assessment Report');

        if (missingDocs.length > 0) {
            Swal.fire({ icon: 'warning', text: `Missing Documents: ${missingDocs.join(', ')}` });
            return false;
        }

        // Field Value Validation
        if (isIndia) {
            if (!vendorData.pan_tan) { Swal.fire({ icon: 'warning', text: 'Please enter PAN/TAN Number.' }); return false; }
            const panVal = validatePAN(vendorData.pan_tan);
            if (!panVal.valid) { Swal.fire({ icon: 'error', text: panVal.error }); return false; }

            if (!isStlDigital) {
                if (!vendorData.gst_number) { Swal.fire({ icon: 'warning', text: 'Please enter GST Number.' }); return false; }
                const gstVal = validateGST(vendorData.gst_number);
                if (!gstVal.valid) { Swal.fire({ icon: 'error', text: gstVal.error }); return false; }

                if (!vendorData.msme_number) { Swal.fire({ icon: 'warning', text: 'Please enter MSME Number.' }); return false; }
            }
        }

        return true;
    };

    const validateUniqueness = async () => {
        try {
            const payload = {
                gst_number: vendorData.gst_number,
                pan_tan_number: vendorData.pan_tan,
                pan_tan_number_status: vendorData.pan_tan_number_status,
                gst_number_status: vendorData.gst_number_status,
                msme_number_status: vendorData.msme_number_status,
                email: vendorData.email,
                phone: vendorData.phone,
                account_number: vendorData.account_number,
                vat_number: vendorData.vat_number,
                vendor_code: vendorCodeRef.current || ''
            };
            const res = await axios.post(`${API_BASE_URL}/api/vendor/validate/`, payload);
            return { ok: true };
        } catch (err) {
            if (err && err.response && err.response.status === 409) {
                return { ok: false, errors: err.response.data || {} };
            }
            return { ok: false, errors: { detail: 'Validation failed. Try again.' } };
        }
    };

    // -------------------------------------------------------------------------
    // NAVIGATION
    // -------------------------------------------------------------------------
    const nextStep = async () => {
        let valid = true;
        if (currentStep === 1) valid = validateStep1();
        if (currentStep === 2) valid = validateStep2();
        if (currentStep === 3) valid = validateStep3();

        if (valid) setCurrentStep(prev => prev + 1);
    };

    const prevStep = () => {
        setCurrentStep(prev => prev - 1);
    };

    // -------------------------------------------------------------------------
    // SUBMIT
    // -------------------------------------------------------------------------
    const handleFinalSubmit = async () => {
        if (!agreedTerms) {
            Swal.fire({ icon: 'warning', text: 'Please agree to the terms and conditions.' });
            return;
        }
        if (captchaInput !== captcha) {
            Swal.fire({ icon: 'error', text: 'Incorrect Captcha.' });
            resetCaptchaField(); // Auto refresh and clear input
            return;
        }

        // Final Logic Checks (Cross Field)
        const isIndia = vendorData.country === 'IN' || !vendorData.country;
        if (isIndia && vendorData.gst_number && vendorData.pan_tan) {
            const gstPanPart = vendorData.gst_number.trim().substring(2, 12);
            if (gstPanPart !== vendorData.pan_tan.trim()) {
                Swal.fire({ icon: 'error', title: 'Mismatch', text: 'GST Number does not match PAN Number.' });
                return;
            }
        }

        // Uniqueness Check
        Swal.fire({
            title: 'Please Wait, While We Process Your Request',
            text: '',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        const uniqueCheck = await validateUniqueness();
        if (!uniqueCheck.ok) {
            Swal.close();
            const errs = uniqueCheck.errors || {};
            let msg = '';
            if (errs.email) msg += `Email: ${errs.email}<br>`;
            if (errs.gst_number) msg += `GST: ${errs.gst_number}<br>`;
            if (errs.pan_tan_number) msg += `PAN: ${errs.pan_tan_number}<br>`;

            Swal.fire({ icon: 'error', title: 'Registration Failed', html: msg || 'Duplicate details found.' });
            return;
        }

        // Submit Data
        const formData = new FormData();

        // Explicitly map frontend keys to backend keys
        // Frontend: firstName -> Backend: first_name
        formData.append('first_name', vendorData.firstName || '');

        // Frontend: lastName -> Backend: last_name
        // Backend now handles empty last_name as valid.
        formData.append('last_name', vendorData.lastName || '');

        // Frontend: address -> Backend: registered_office_address
        formData.append('registered_office_address', vendorData.address || '');

        // Frontend: pan_tan -> Backend: pan_tan_number
        formData.append('pan_tan_number', vendorData.pan_tan || '');
        formData.append('pan_tan_number_status', vendorData.pan_tan_number_status || 'Not Verified');
        formData.append('gst_number_status', vendorData.gst_number_status || 'Not Verified');
        formData.append('msme_number_status', vendorData.msme_number_status || 'Not Verified');

        // Direct mappings (keys match or close enough)
        formData.append('email', vendorData.email || '');
        formData.append('phone', vendorData.phone || '');
        formData.append('gst_number', vendorData.gst_number || '');
        formData.append('vat_number', vendorData.vat_number || '');
        formData.append('msme_number', vendorData.msme_number || '');
        formData.append('city', vendorData.city || '');
        formData.append('country', vendorData.country || '');
        formData.append('postal_code', vendorData.postal_code || '');
        formData.append('region', vendorData.region || '');
        formData.append('contact_person_name', vendorData.contactName || '');

        // Bank details
        formData.append('account_holder_name', vendorData.account_holder_name || '');
        formData.append('account_number', vendorData.account_number || '');
        formData.append('ifsc_code', vendorData.ifsc_code || '');
        formData.append('iban_number', vendorData.iban_number || '');
        formData.append('bank_control_key', vendorData.bank_control_key || '');
        formData.append('bank_name', vendorData.bank_name || '');
        formData.append('bank_account_name', vendorData.bank_account_name || '');
        formData.append('bank_country', vendorData.bank_country || '');
        formData.append('bank_state', vendorData.bank_state || '');
        formData.append('bank_address', vendorData.bank_address || '');
        formData.append('bank_city', vendorData.bank_city || '');
        formData.append('bank_branch', vendorData.bank_branch || '');

        // Extra fields
        formData.append('district', vendorData.district || '');
        formData.append('house_number', vendorData.house_number || '');
        formData.append('cin_number', vendorData.cin_number || '');
        formData.append('tin_number', vendorData.tin_number || '');
        formData.append('registered_office_address_ext', vendorData.registered_office_address_ext || '');

        // Vendor Code
        formData.append('vendor_code', vendorCodeRef.current);

        // Files
        if (vendorFiles.pan_tan_file) formData.append('pan_tan_file', vendorFiles.pan_tan_file);
        if (vendorFiles.gst_file) formData.append('gst_file', vendorFiles.gst_file);
        if (vendorFiles.msme_certificate) formData.append('msme_certificate', vendorFiles.msme_certificate);
        if (vendorFiles.passbook) formData.append('passbook', vendorFiles.passbook);
        if (vendorFiles.canceled_cheque) formData.append('cancel_cheque', vendorFiles.canceled_cheque);
        if (vendorFiles.assessment_report) formData.append('assessment_report', vendorFiles.assessment_report);
        if (vendorFiles.nda_document) formData.append('nda_report', vendorFiles.nda_document);

        try {
            // Updated endpoint to match backend 'vendor_register'
            await axios.post(`${API_BASE_URL}/api/vendor/register/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            Swal.close();
            // Clear storage
            localStorage.removeItem(`vendor_regist_${vendorCodeRef.current}`);
            // Clear the stored vendor code so new registrations can store their own code
            localStorage.removeItem('last_valid_vendor_code');
            console.log('✅ Cleared vendor code from localStorage after successful registration');
            setIsSuccess(true);
        } catch (error) {
            Swal.close();
            Swal.fire({ icon: 'error', title: 'Error', text: 'Registration failed. Please try again.' });
            console.error(error);
        }
    };


    if (isSuccess) {
        return <RegistrationSuccess vendorCode={vendorCode} />;
    }

    if (isLoading) {
        return <div className="loading-spinner"><FaSpinner className="fa-spin" /> Loading...</div>;
    }

    return (
        <>
            {/* FIXED HEADER */}
            <div className="register-header-fixed">
                {vendorOnboardInfo?.logo && (
                    <img draggable={false}
                        src={vendorOnboardInfo.logo}
                        alt="Company Logo"
                        className="logo-header"
                    />
                )}
                {!vendorOnboardInfo?.logo && <h2>Vendor Registration</h2>}
            </div>

            <div className="register-container">
                <div style={{ width: '95%', maxWidth: '1200px', margin: '0 auto' }}>
                    <StepProgressBar currentStep={currentStep} steps={[
                        { id: 1, label: 'Organization' },
                        { id: 2, label: 'Bank Details' },
                        { id: 3, label: 'Tax & Compliance' },
                        { id: 4, label: 'Review & Submit' }
                    ]} />
                </div>

                <div className="step-content">
                    {currentStep === 1 && (
                        <Step1_Organization
                            vendorData={vendorData}
                            handleVendorChange={handleVendorChange}
                            isDisabled={isLoading || isLinkBlocked}
                            vendorOnboardInfo={vendorOnboardInfo}
                        />
                    )}
                    {currentStep === 2 && (
                        <Step2_Bank
                            vendorData={vendorData}
                            handleVendorChange={handleVendorChange}
                            isDisabled={isLoading || isLinkBlocked}
                            vendorOnboardInfo={vendorOnboardInfo}
                            uploaded={uploaded}
                            fileNames={fileNames}
                            handleFileChange={handleFileChange}
                            handleRemoveFile={handleRemoveFile}
                            passbookRef={passbookRef}
                            chequeRef={chequeRef}
                        />
                    )}
                    {currentStep === 3 && (
                        <Step3_Tax
                            vendorData={vendorData}
                            handleVendorChange={handleVendorChange}
                            isDisabled={isLoading || isLinkBlocked}
                            vendorOnboardInfo={vendorOnboardInfo}
                            uploaded={uploaded}
                            fileNames={fileNames}
                            handleFileChange={handleFileChange}
                            handleStandardFileChange={handleStandardFileChange}
                            handleRemoveFile={handleRemoveFile}
                            panFileRef={panFileRef}
                            gstFileRef={gstFileRef}
                            msmeRef={msmeRef}
                            assessmentRef={assessmentRef}
                            ndaRef={ndaRef}
                        />
                    )}
                    {currentStep === 4 && (
                        <Step4_Review
                            vendorData={vendorData}
                            uploaded={uploaded}
                            fileNames={fileNames}
                            agreedTerms={agreedTerms}
                            setAgreedTerms={setAgreedTerms}
                            captcha={captcha}
                            captchaInput={captchaInput}
                            setCaptchaInput={setCaptchaInput}
                            generateCaptcha={generateCaptcha}
                            isDisabled={isLoading || isLinkBlocked}
                            vendorOnboardInfo={vendorOnboardInfo}
                            onShowTerms={fetchTermsAndConditions}
                        />
                    )}
                </div>

                {/* Navigation Footer */}
                <div className="step-footer" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
                    {currentStep > 1 ? (
                        <button
                            className="btn-back"
                            onClick={prevStep}
                            disabled={isLinkBlocked || isLoading}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '6px',
                                border: '1px solid #ccc',
                                backgroundColor: (isLinkBlocked || isLoading) ? '#e5e7eb' : '#fff',
                                cursor: (isLinkBlocked || isLoading) ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: (isLinkBlocked || isLoading) ? 0.6 : 1
                            }}
                        >
                            <FaArrowLeft /> Back
                        </button>
                    ) : (
                        <div></div> /* Placeholder to keep Next button on right if Flex used */
                    )}

                    {currentStep < 4 ? (
                        <button
                            className="btn-next"
                            onClick={nextStep}
                            disabled={isLinkBlocked || isLoading}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '6px',
                                backgroundColor: (isLinkBlocked || isLoading) ? '#9ca3af' : '#4f46e5',
                                color: '#fff',
                                border: 'none',
                                cursor: (isLinkBlocked || isLoading) ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: (isLinkBlocked || isLoading) ? 0.7 : 1
                            }}
                        >
                            Next <FaArrowRight />
                        </button>
                    ) : (
                        <button
                            className="btn-submit"
                            onClick={handleFinalSubmit}
                            disabled={isLinkBlocked || isLoading}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '6px',
                                backgroundColor: (isLinkBlocked || isLoading) ? '#9ca3af' : '#059669',
                                color: '#fff',
                                border: 'none',
                                cursor: (isLinkBlocked || isLoading) ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: (isLinkBlocked || isLoading) ? 0.7 : 1
                            }}
                        >
                            Submit Registration <FaCheck />
                        </button>
                    )}
                </div>

                {/* 🆕 Updated Loader: Middle Screen Card with Dark Backdrop */}
                {isOcrLoading && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dim dark backdrop
                        zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <div style={{
                            background: '#fff',
                            padding: '30px 50px',
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center'
                        }}>
                            <FaSpinner className="spinner icon-spin" style={{ fontSize: '3rem', color: '#4f46e5', marginBottom: '1.5rem' }} />
                            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Please Wait,</h3>
                            <span style={{ fontSize: '1.1rem', color: '#555' }}>While We Process Your Request</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div style={{ position: 'fixed', bottom: 0, left: 0, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px 0', zIndex: 1000 }}>
                <span style={{ fontWeight: '600', color: '#fff', marginRight: '10px' }}>Powered by</span>
                <img draggable={false} src="/Main.png" alt="STL-DIGITAL" style={{ height: '30px' }} />
            </div>

            {/* TERMS MODAL */}
            {showTerms && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: '#fff', width: '90%', maxWidth: '800px',
                        maxHeight: '80vh', borderRadius: '8px', display: 'flex', flexDirection: 'column',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0 }}>Terms and Conditions</h3>
                            <button onClick={handleCloseTerms} style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                        </div>
                        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, lineHeight: '1.6' }}>
                            {loadingTerms ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <FaSpinner className="icon-spin" style={{ fontSize: '2rem', color: '#4f46e5' }} />
                                    <p>Loading Terms...</p>
                                </div>
                            ) : (
                                <div dangerouslySetInnerHTML={{ __html: termsContent }} />
                            )}
                        </div>
                        <div style={{ padding: '20px', borderTop: '1px solid #eee', textAlign: 'right' }}>
                            <button
                                onClick={handleCloseTerms}
                                style={{
                                    padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff',
                                    border: 'none', borderRadius: '6px', cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
